import { FC, useContext } from "react";

import { Grid, useTheme } from "@mui/material";
import {
  StripeRecurringInterval,
  formatCurrencyAmount,
  getDollarValue,
  getDatePlusNDays,
  getDatePlusNMonths,
  getDatePlusNYears,
  FormType,
  SubscriptionChargeDay,
} from "@simplyk/common";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getDateInTimezone } from "../../../canvas/forms-design-system/FormDate/helper";
import { FrontendFormContext } from "../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../common/contexts/LocaleContext";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { getChargeDayRenewalDate } from "@/common/helpers/getChargeDayRenewalDate";
import { useTranslate } from "@/common/hooks/useTranslate";
import { MembershipRenewalBuyerInfo } from "@/common/types/ticketing";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const PaymentProcessorTotalWithTip: FC = () => {
  const theme = useTheme();
  const { membershipRenewalBuyerInfo, formObject: ticketing } = useTicketingFormV2ContextOrDefaults();
  const { displayedFormAmount, tip, stripeRecurrenceInterval, currency, formType } = useContext(FrontendFormContext);

  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();
  const amount = getDollarValue(displayedFormAmount + tip);

  const isFormV2 = theme.constants.isFormV2;

  const renewalDate = getRenewalDate(membershipRenewalBuyerInfo, stripeRecurrenceInterval, formType);
  const formattedRenewalDate = renewalDate
    ? getDateInTimezone(renewalDate, ticketing.eventTimezone).toLocaleString(
        {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZoneName: "short",
        },
        { locale: isoLocale }
      )
    : undefined;

  return (
    <Grid
      item
      xs={12}
      sx={{
        borderTop: `1px solid ${isFormV2 ? theme.palette.border.form.moderate : theme.palette.neutral[90]}`,
        backgroundColor: isFormV2 ? theme.palette.surface.form.intense : theme.palette.grey[100],
        padding: theme.spacing(2),
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <DashboardTypography
            variant="body1"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: isFormV2 ? theme.palette.text.form.intense : undefined,
            }}
          >
            {t("donationForm", "total")}
          </DashboardTypography>
        </Grid>
        <Grid item xs={8} data-test="payment-processor-total-with-tip-amount">
          <DashboardTypography
            variant="body1"
            align="right"
            component="div"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: isFormV2 ? theme.palette.text.form.intense : undefined,
            }}
          >
            {currency && formatCurrencyAmount({ value: amount, locale: isoLocale, currency })}
            {stripeRecurrenceInterval &&
              ` / ${
                stripeRecurrenceInterval === StripeRecurringInterval.year
                  ? t("common", "common.year")
                  : stripeRecurrenceInterval === StripeRecurringInterval.quarter
                    ? t("common", "common.quarter")
                    : t("common", "common.month")
              }`}
          </DashboardTypography>
          {stripeRecurrenceInterval && formattedRenewalDate && (
            <Grid item xs={12}>
              <Grid container justifyContent="flex-end">
                <DashboardTypography
                  vibe="text-form-supershy"
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "right",
                  }}
                  data-test="payment-processor-total-with-tip-renew-date"
                >
                  {t("ticketing", "payment.total.recurring", { date: formattedRenewalDate })}
                </DashboardTypography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export const getRenewalDate = (
  membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined,
  stripeRecurrenceInterval: StripeRecurringInterval | null | undefined,
  formType: FormType,
  chargeDay?: SubscriptionChargeDay | null
): Date | null => {
  if (!stripeRecurrenceInterval) {
    return null;
  }

  // the shouldProposeAutomaticRenewal only affects the yearly subscription
  if (
    !membershipRenewalBuyerInfo?.shouldProposeAutomaticRenewal &&
    stripeRecurrenceInterval === StripeRecurringInterval.year
  ) {
    return null;
  }

  if (membershipRenewalBuyerInfo?.expirationDate) {
    return new Date(membershipRenewalBuyerInfo.expirationDate);
  }

  if (formType === FormType.DonationForm && chargeDay) {
    return getChargeDayRenewalDate(stripeRecurrenceInterval, chargeDay);
  }

  if (formType === FormType.DonationForm && stripeRecurrenceInterval) {
    switch (stripeRecurrenceInterval) {
      case StripeRecurringInterval.week:
        return getDatePlusNDays(new Date(), 7);
      case StripeRecurringInterval.biWeek:
        return getDatePlusNDays(new Date(), 14);
      case StripeRecurringInterval.month:
        return getDatePlusNMonths(new Date(), 1);
      case StripeRecurringInterval.quarter:
        return getDatePlusNMonths(new Date(), 3);
      case StripeRecurringInterval.year:
        return getDatePlusNYears(new Date(), 1);
      default:
        return null;
    }
  }

  return null;
};
